import {
  cloneData, cloneDataOrNull, DataModelBuilder, isSameData,
  SerializationContext, SerializedData,
} from '../../dto/dto.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { Price } from '../price.js';
import { TicketTemplate } from '../ticket-template';
import { CartItemDto } from './cart-item.js';
import { TicketCartItemDto } from './ticket-cart-item.js';
import { TicketSupplementTemplate } from '../ticket-supplement-template.js';
import { VersionRange } from '../../core/version-range.js';

export class TicketCartItemWithQuantityDto extends TicketCartItemDto {
  static readonly TYPE_ID: string = '2e9fa2e91b8d42269dc4d39c02ca3558';

  static readonly SCHEMA_VERSION: number = 4;

  public readonly quantity: number;
  private _totalPrice: Price;

  private constructor(
      eventId: string,
      quantity: number,
      ticket: TicketTemplate,
      supplement: TicketSupplementTemplate | null,
      priceModifier: Price | null
  ) {
    super(eventId, ticket, supplement, priceModifier);

    this.quantity = quantity;
    this._totalPrice = this.unitPrice.scale(quantity);
  }

  get $typeId(): string {
    return TicketCartItemWithQuantityDto.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    // Version should always be `exactly`.
    // Cart should never be backwards migrated, backend should be updated before frontend.
    return VersionRange.exactly(TicketCartItemWithQuantityDto.SCHEMA_VERSION);
  }

  get totalPrice(): Price {
    return this._totalPrice;
  }

  static create(args: {
    eventId: string, // this should be mandatory, we will make the backing field non nullable in a future release
    quantity: number,
    ticket: TicketTemplate,
    supplement: TicketSupplementTemplate | null,
    priceModifier: Price | null
  }) {
    return new TicketCartItemWithQuantityDto(
        args.eventId,
        args.quantity,
        args.ticket,
        args.supplement,
        args.priceModifier,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): TicketCartItemWithQuantityDto {
    return new TicketCartItemWithQuantityDto(
        MapUtils.getString(map, 'eventId'),
        MapUtils.getInteger(map, 'quantity'),
        context.fromData(MapUtils.getRecord(map, 'ticket')),
        context.fromDataOrNull<TicketSupplementTemplate>(MapUtils.getRecordOrNull(map, 'supplement')),
        Price.decodeOrNull(MapUtils.getStringOrNull(map, 'priceModifier')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'eventId': this.eventId,
      'quantity': this.quantity,
      'ticket': context.toData(this.ticket),
      'supplement': context.toDataOrNull(this.supplement),
      'priceModifier': this.priceModifier?.encode() ?? null,
    };
  }

  clone(): this {
    return new TicketCartItemWithQuantityDto(
        this.eventId,
        this.quantity,
        cloneData(this.ticket),
        cloneDataOrNull(this.supplement),
        cloneDataOrNull(this.priceModifier),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.eventId === this.eventId &&
      other.quantity === this.quantity &&
      isSameData(other.ticket, this.ticket) &&
      isSameData(other.supplement, this.supplement) &&
      isSameData(other.priceModifier, this.priceModifier);
  }

  static override assertType(item: CartItemDto): item is TicketCartItemWithQuantityDto {
    return item instanceof TicketCartItemWithQuantityDto;
  }
}

export class TicketCartItemWithQuantityDtoBuilder extends DataModelBuilder<TicketCartItemWithQuantityDto> {
  build(context: SerializationContext, map: SerializedData): TicketCartItemWithQuantityDto {
    return TicketCartItemWithQuantityDto.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(TicketCartItemWithQuantityDto.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(TicketCartItemWithQuantityDto.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     * - add `supplement` of type `TicketSupplementTemplate | null`.
     */
    if(version < 2) {
      version = 2;
    }

    /**
     * Version 3:
     * - add `eventId` of type `string | null`.
     */
    if(version < 3) {
      version = 3;
    }

    /**
     * Version 4:
     * - changed `eventId` from `string | null` to `string`.
     *   we don't need to do any migrations, we previously released a version where the event id was non-nullable
     *   in the constructor
     */
    if(version < 4) version = 4;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
